import { Button, TYPE_LINK } from '@eventbrite/eds-button';
import React, { FunctionComponent, MouseEventHandler } from 'react';
import TicketCardStatus, {
    TicketCardStatusProps,
} from '../../../components/ticketsSelectionList/TicketCardStatus';
import TicketDonationField, {
    TicketDonationFieldProps,
} from '../../../components/ticketsSelectionList/TicketDonationField';
import TicketSelectorSwitcher, {
    ConnectedTicketSelectorSwitcherProps,
} from '../../../components/ticketsSelectionList/TicketSelectorSwitcher';
import { CTA_JOIN_WAITLIST, TicketClassCategory } from '../../../constants';
import { TicketReseller } from '../../../models/tickets';
import { isDonationTicket } from '../../../utils/tickets';
import { shouldShowTicketReseller } from '../utils/ticketUtils';

export interface TicketAvailabilityFieldProps
    extends Omit<
            ConnectedTicketSelectorSwitcherProps,
            'onQuantityChange' | 'isDisabled'
        >,
        Omit<
            TicketCardStatusProps,
            'shouldHideStatus' | 'customClassNames' | 'textSize'
        >,
        Omit<TicketDonationFieldProps, 'isDisabled' | 'onChange' | 'amount'> {
    canBeSelected: boolean;
    category: TicketClassCategory;
    donationAmount: TicketDonationFieldProps['amount'];
    hideSaleDates: boolean;
    isFormSubmitting: boolean;
    onDonationChange: TicketDonationFieldProps['onChange'];
    onJoinWaitlist: MouseEventHandler<HTMLButtonElement> | null;
    onQuantityChange: (variantId: string | null, quantity: string) => void;
    showJoinWaitlist: boolean;
    ticketReseller: TicketReseller | undefined;
    variantId: string | null;
}

export const TicketAvailabilityField: FunctionComponent<
    TicketAvailabilityFieldProps
> = ({
    canBeSelected,
    id,
    statusKey,
    showJoinWaitlist,
    onJoinWaitlist,
    hideSaleDates,
    ticketReseller = {},
    isFormSubmitting,
    onQuantityChange,
    variantId,
    isTicketTiered,
    salesEnd,
    salesStart,
    donationAmount,
    currencySymbol,
    currencyFormat,
    isCurrencySuffix,
    selectedQuantity,
    ticketOptionsRange,
    displayPrice,
    onDonationChange,
    name,
    category,
}) => {
    // Only display individual ticket reseller if other tickets
    // have remaining quantity and resellers are available.
    const showTicketReseller = shouldShowTicketReseller(
        statusKey,
        ticketReseller,
    );

    if (canBeSelected) {
        if (category && isDonationTicket({ category })) {
            return (
                <div className="eds-ticket-card-content__donation-field">
                    <TicketDonationField
                        id={id}
                        isDisabled={isFormSubmitting}
                        onChange={onDonationChange}
                        amount={donationAmount}
                        currencySymbol={currencySymbol}
                        currencyFormat={currencyFormat}
                        isCurrencySuffix={isCurrencySuffix}
                    />
                </div>
            );
        }

        return (
            <TicketSelectorSwitcher
                id={id}
                name={name}
                selectedQuantity={selectedQuantity}
                ticketOptionsRange={ticketOptionsRange}
                displayPrice={displayPrice}
                isDisabled={isFormSubmitting}
                onQuantityChange={(quantity) =>
                    onQuantityChange(variantId, quantity)
                }
            />
        );
    }

    if (showJoinWaitlist) {
        return (
            <div
                className="eds-ticket-card-content__join-waitlist-button"
                data-spec="join-waitlist-button"
                data-automation={`join-waitlist-ticket-${id}-btn`}
            >
                <Button onClick={onJoinWaitlist}>{CTA_JOIN_WAITLIST}</Button>
            </div>
        );
    }

    if (showTicketReseller && ticketReseller) {
        return (
            <div data-spec="show-ticket-reseller">
                <Button
                    type={TYPE_LINK}
                    href={ticketReseller.url}
                    target="_blank"
                    data-spec="ticket-reseller-button"
                >
                    {ticketReseller.text}
                </Button>
            </div>
        );
    }

    return (
        <TicketCardStatus
            isTicketTiered={isTicketTiered}
            salesEnd={salesEnd}
            salesStart={salesStart}
            statusKey={statusKey}
            shouldHideStatus={hideSaleDates}
            customClassNames="eds-text--right"
        />
    );
};

export default TicketAvailabilityField;
